import type { Channel, ChannelMember, ChannelUpdate } from "@/channel/channel-types";
import type { MessageReaction, MessageWithDetails } from "@/message/message-types";
import type { Workspace, WorkspaceMember } from "@/workspace/workspace-types";
import type { IWorksheetData } from "@univerjs/presets";

export enum ServerWsEvent {
	ConnectionOpened = "ConnectionOpened",
	ConnectionClosed = "ConnectionClosed",
	Error = "Error",
	ServerHeartbeat = "ServerHeartbeat",
	UserHeartbeat = "UserHeartbeat",
	Notifications = "Notifications",
	Intervals = "Intervals",
	RoomBroadcast = "RoomBroadcast",
	RoomExited = "RoomExited",
	RoomJoined = "RoomJoined",
	RoomCreated = "RoomCreated",
	RoomDestroyed = "DestroyRoom",
	RoomOwnerChanged = "RoomOwnerChanged",
	ChannelCreated = "ChannelCreated",
	ChannelUpdated = "ChannelUpdated",
	ChannelOrderChanged = "ChannelOrderChanged",
	ChannelDeleted = "ChannelDeleted",
	ChannelMemberAdded = "ChannelMemberAdded",
	ChannelMemberRemoved = "ChannelMemberRemoved",
	ChannelMemberRoleChanged = "ChannelMemberRoleChanged",
	ChannelArchived = "ChannelArchived",
	ChannelUnarchived = "ChannelUnarchived",
	ChannelNameUpdated = "ChannelNameUpdated",
	WorkspaceCreated = "WorkspaceCreated",
	WorkspaceUpdated = "WorkspaceUpdated",
	WorkspaceDeleted = "WorkspaceDeleted",
	WorkspaceMemberAdded = "WorkspaceMemberAdded",
	WorkspaceMemberRemoved = "WorkspaceMemberRemoved",
	WorkspaceMemberLeft = "WorkspaceMemberLeft",
	WorkspaceMemberRoleChanged = "WorkspaceMemberRoleChanged",
	MessageCreated = "MessageCreated",
	MessageUpdated = "MessageUpdated",
	MessageDeleted = "MessageDeleted",
	ReactionAdded = "ReactionAdded",
	ReactionRemoved = "ReactionRemoved",
	DocUpdated = "DocUpdated",
	AwarenessUpdated = "AwarenessUpdated",
	CollabStateUpdated = "CollabStateUpdated",
	CollabDisconnectUpdated = "CollabDisconnectUpdated",
	ElementStateUpdated = "ElementStateUpdated",
	SelectionUpdated = "SelectionUpdated",
	RequestSelection = "RequestSelection",
	CellUpdated = "CellUpdated",
	SheetCreated = "SheetCreated",
	SheetDeleted = "SheetDeleted",
	SheetRenamed = "SheetRenamed",
}

type BaseEvent = {
	from: string;
	serverId: string;
};

export type ServerEventMap = {
	[ServerWsEvent.ConnectionOpened]: BaseEvent & {
		userId: string;
	};
	[ServerWsEvent.Error]: BaseEvent & { message: string };
	[ServerWsEvent.ConnectionClosed]: BaseEvent & {
		userId: string;
	};
	[ServerWsEvent.RoomBroadcast]: BaseEvent & {
		roomId: string;
		payload: any;
	};
	[ServerWsEvent.RoomExited]: BaseEvent & {
		roomId: string;
		userId: string;
	};
	[ServerWsEvent.RoomJoined]: BaseEvent & {
		roomId: string;
		userId: string;
	};
	[ServerWsEvent.RoomCreated]: BaseEvent & {
		roomId: string;
		ownerId: string;
	};
	[ServerWsEvent.RoomOwnerChanged]: BaseEvent & {
		roomId: string;
		ownerId: string;
	};
	[ServerWsEvent.RoomDestroyed]: BaseEvent & {
		roomId: string;
	};
	[ServerWsEvent.Notifications]: BaseEvent & {
		message: string;
	};
	[ServerWsEvent.Intervals]: BaseEvent & { message: string };
	[ServerWsEvent.ServerHeartbeat]: BaseEvent & { serverId: string };
	[ServerWsEvent.UserHeartbeat]: BaseEvent & {
		totalUsers: number;
	};
	[ServerWsEvent.ChannelCreated]: {
		channel: Channel;
		channelId: number;
		workspaceId: number;
		createdBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.ChannelUpdated]: {
		channelId: number;
		workspaceId: number;
		channel: Partial<Channel>;
		updatedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.ChannelOrderChanged]: {
		workspaceId: number;
		updates: ChannelUpdate[];
		updatedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.ChannelNameUpdated]: {
		channelId: number;
		workspaceId: number;
		channel: Partial<Channel>;
		updatedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.ChannelDeleted]: {
		channelId: number;
		workspaceId: number;
		deletedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.ChannelMemberAdded]: {
		channelId: number;
		workspaceId: number;
		member: ChannelMember;
		addedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.ChannelMemberRemoved]: {
		channelId: string;
		workspaceId: number;
		userId: string;
		removedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.ChannelMemberRoleChanged]: {
		channelId: string;
		workspaceId: number;
		userId: string;
		oldRole: string;
		newRole: string;
		changedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.ChannelArchived]: {
		channelId: string;
		workspaceId: number;
		archivedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.ChannelUnarchived]: {
		channelId: string;
		workspaceId: number;
		unarchivedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.WorkspaceCreated]: {
		workspace: Workspace;
		workspaceId: number;
		createdBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.WorkspaceUpdated]: {
		workspaceId: number;
		workspace: Partial<Workspace>;
		updatedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.WorkspaceDeleted]: {
		workspaceId: number;
		deletedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.WorkspaceMemberAdded]: {
		workspaceId: number;
		workspaceTitle: string;
		member: WorkspaceMember;
		addedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.WorkspaceMemberRemoved]: {
		workspaceId: number;
		workspaceTitle: string;
		userId: string;
		removedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.WorkspaceMemberLeft]: {
		workspaceId: number;
		workspaceTitle: string;
		userId: string;
		timestamp: Date;
	};
	[ServerWsEvent.WorkspaceMemberRoleChanged]: {
		workspaceId: number;
		userId: string;
		oldRole: string;
		newRole: string;
		changedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.MessageCreated]: {
		message: MessageWithDetails;
		channelId: number;
		workspaceId: number;
		userId: string;
		userName: string;
	};
	[ServerWsEvent.MessageUpdated]: {
		message: MessageWithDetails;
		channelId: number;
		workspaceId: number;
		userId: string;
		userName: string;
	};
	[ServerWsEvent.MessageDeleted]: {
		messageId: number;
		channelId: number;
		workspaceId: number;
		userId: string;
		userName: string;
	};
	[ServerWsEvent.ReactionAdded]: {
		reaction: MessageReaction;
		messageId: number;
		channelId: number;
		workspaceId: number;
		userId: string;
		userName: string;
	};
	[ServerWsEvent.ReactionRemoved]: {
		messageId: number;
		emoji: string;
		channelId: number;
		workspaceId: number;
		userId: string;
		userName: string;
	};
	[ServerWsEvent.DocUpdated]: {
		channelId: number;
		update: string;
		stateVector: string;
		updatedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.AwarenessUpdated]: {
		channelId: number;
		update: string;
		stateVector: string;
		updatedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.CollabStateUpdated]: {
		channelId: number;
		collabState: any;
		username: string;
		updatedBy: string;
	};
	[ServerWsEvent.CollabDisconnectUpdated]: {
		channelId: number;
		username: string;
		updatedBy: string;
	};
	[ServerWsEvent.ElementStateUpdated]: {
		channelId: number;
		elementState: any;
		username: string;
		updatedBy: string;
	};
	[ServerWsEvent.SelectionUpdated]: {
		channelId: number;
		worksheet: string;
		selection: string;
		username: string;
		updatedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.RequestSelection]: {
		channelId: number;
		worksheet: string;
		username: string;
		updatedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.CellUpdated]: {
		channelId: number;
		worksheet: string;
		value: string;
		formula: string;
		range: string;
		clear: boolean;
		updatedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.SheetCreated]: {
		channelId: number;
		worksheet: IWorksheetData;
		updatedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.SheetDeleted]: {
		channelId: number;
		worksheetId: string;
		updatedBy: string;
		timestamp: Date;
	};
	[ServerWsEvent.SheetRenamed]: {
		channelId: number;
		worksheet: IWorksheetData;
		updatedBy: string;
		timestamp: Date;
	}
};

export const channelEvents = [
	ServerWsEvent.ChannelDeleted,
	ServerWsEvent.ChannelCreated,
	ServerWsEvent.ChannelUpdated,
	ServerWsEvent.ChannelOrderChanged,
	ServerWsEvent.ChannelMemberAdded,
	ServerWsEvent.ChannelMemberRemoved,
	ServerWsEvent.ChannelMemberRoleChanged,
	ServerWsEvent.ChannelArchived,
	ServerWsEvent.ChannelUnarchived,
	ServerWsEvent.ChannelNameUpdated,
];

export const workspaceEvents = [
	ServerWsEvent.WorkspaceCreated,
	ServerWsEvent.WorkspaceUpdated,
	ServerWsEvent.WorkspaceDeleted,
	ServerWsEvent.WorkspaceMemberAdded,
	ServerWsEvent.WorkspaceMemberRemoved,
	ServerWsEvent.WorkspaceMemberLeft,
	ServerWsEvent.WorkspaceMemberRoleChanged,
];

export const messageEvents = [
	ServerWsEvent.MessageCreated,
	ServerWsEvent.MessageUpdated,
	ServerWsEvent.MessageDeleted,
	ServerWsEvent.ReactionAdded,
	ServerWsEvent.ReactionRemoved,
];

export const docEvents = [
	ServerWsEvent.DocUpdated,
	ServerWsEvent.AwarenessUpdated,
];

export const boardEvents = [
	ServerWsEvent.CollabStateUpdated,
	ServerWsEvent.CollabDisconnectUpdated,
	ServerWsEvent.ElementStateUpdated,
];

export const sheetEvents = [
	ServerWsEvent.SelectionUpdated,
	ServerWsEvent.RequestSelection,
	ServerWsEvent.CellUpdated,
	ServerWsEvent.SheetCreated,
	ServerWsEvent.SheetDeleted,
	ServerWsEvent.SheetRenamed,
];